/**
 * Company Switcher Component
 *
 * ISO 27001 A.9.2.3 - Gestion des accès multi-company
 * Permet à l'utilisateur de changer de société active
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, ChevronDown, Check } from 'lucide-react';
import { companiesAPI } from '@/utils/api';
import { logger } from '@/utils/logger';
import { useAuthStore } from '@/stores/authStore';
import { Spinner } from './Spinner';

interface CompanyInfo {
  id: string;
  name: string;
  tradeName: string | null;
  siret: string | null;
  logoUrl: string | null;
  role: string;
  isDefault: boolean;
}

interface QuotaInfo {
  current: number;
  limit: number;
  canCreate: boolean;
}

export interface CompanySwitcherProps {
  isCollapsed?: boolean;
  className?: string;
}

export const CompanySwitcher: React.FC<CompanySwitcherProps> = ({
  isCollapsed = false,
  className = '',
}) => {
  const { t } = useTranslation();
  const [companies, setCompanies] = useState<CompanyInfo[]>([]);
  const [currentCompanyId, setCurrentCompanyId] = useState<string | null>(null);
  const [quota, setQuota] = useState<QuotaInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { user, setUser } = useAuthStore();

  const currentCompany = companies.find((c) => c.id === currentCompanyId);

  // Load companies on mount
  useEffect(() => {
    loadCompanies();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadCompanies = async () => {
    try {
      setLoading(true);
      const data = await companiesAPI.getMyCompanies();
      setCompanies(data.companies || []);
      setCurrentCompanyId(data.currentCompanyId || user?.companyId || null);
      setQuota(data.quota || null);
    } catch (error) {
      logger.error('Erreur chargement sociétés:', error);
      setCompanies([]);
      setQuota(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSwitchCompany = async (companyId: string) => {
    if (companyId === currentCompanyId || switching) return;

    try {
      setSwitching(true);
      const result = await companiesAPI.switchCompany(companyId);

      // Update local state
      setCurrentCompanyId(companyId);

      // Update auth store with new companyId
      if (user) {
        setUser({ ...user, companyId });
      }

      setIsOpen(false);

      // Reload the page to refresh data with new company context
      window.location.reload();
    } catch (error: any) {
      logger.error('Erreur changement de société:', error);
    } finally {
      setSwitching(false);
    }
  };

  const getRoleBadge = (role: string) => {
    const roleLabels: Record<string, { label: string; color: string }> = {
      OWNER: { label: t('common.ui.companySwitcher.roles.OWNER'), color: 'bg-purple-100 text-purple-700' },
      ADMIN: { label: t('common.ui.companySwitcher.roles.ADMIN'), color: 'bg-blue-100 text-blue-700' },
      MANAGER: { label: t('common.ui.companySwitcher.roles.MANAGER'), color: 'bg-teal-100 text-teal-700' },
      ACCOUNTANT: { label: t('common.ui.companySwitcher.roles.ACCOUNTANT'), color: 'bg-amber-100 text-amber-700' },
      MEMBER: { label: t('common.ui.companySwitcher.roles.MEMBER'), color: 'bg-gray-100 text-gray-700' },
      VIEWER: { label: t('common.ui.companySwitcher.roles.VIEWER'), color: 'bg-gray-100 text-gray-600' },
    };
    const config = roleLabels[role] || roleLabels.MEMBER;
    return (
      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className={`flex items-center gap-2 ${isCollapsed ? 'justify-center' : ''} ${className}`}>
        <Spinner size="sm" />
      </div>
    );
  }

  // No companies
  if (companies.length === 0) {
    return null;
  }

  // Single company - style ProfileWidget, no switcher needed
  if (companies.length === 1) {
    return (
      <div className={`flex items-center gap-3 bg-white/40 backdrop-blur-xl rounded-full shadow-lg border border-white/40 hover:bg-white/70 hover:shadow-xl transition-all duration-300 ${isCollapsed ? 'p-2 justify-center' : 'px-4 py-2.5'} ${className}`}>
        {currentCompany?.logoUrl ? (
          <img
            src={currentCompany.logoUrl}
            alt={currentCompany.name}
            className="w-9 h-9 rounded-full object-contain bg-white flex-shrink-0 shadow-md"
          />
        ) : (
          <div className="w-9 h-9 bg-gradient-to-br from-teal-600 to-teal-700 rounded-full flex items-center justify-center flex-shrink-0 shadow-md">
            <Building2 className="w-4 h-4 text-white" />
          </div>
        )}
        {!isCollapsed && (
          <div className="flex-1 text-left min-w-0">
            <p className="text-sm font-bold text-gray-900 truncate">
              {currentCompany?.name || t('common.ui.companySwitcher.myCompany')}
            </p>
            {currentCompany?.tradeName && currentCompany.tradeName !== currentCompany.name ? (
              <p className="text-xs text-gray-600 font-medium truncate">{currentCompany.tradeName}</p>
            ) : (
              <p className="text-xs text-gray-600 font-medium">{t('common.ui.companySwitcher.company')}</p>
            )}
          </div>
        )}
      </div>
    );
  }

  // Multiple companies - show switcher
  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      {/* Trigger Button - style ProfileWidget */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={switching}
        className={`
          w-full flex items-center gap-3 bg-white/40 backdrop-blur-xl rounded-full
          shadow-lg border border-white/40
          hover:bg-white/70 hover:shadow-xl
          transition-all duration-300 group cursor-pointer
          ${isCollapsed ? 'p-2 justify-center' : 'px-4 py-2.5'}
          ${isOpen ? 'bg-white/70 shadow-xl border-teal-200' : ''}
        `}
      >
        {/* Company Logo/Icon */}
        {currentCompany?.logoUrl ? (
          <img
            src={currentCompany.logoUrl}
            alt={currentCompany.name}
            className="w-9 h-9 rounded-full object-contain bg-white flex-shrink-0 shadow-md"
          />
        ) : (
          <div className="w-9 h-9 bg-gradient-to-br from-teal-600 to-teal-700 rounded-full flex items-center justify-center flex-shrink-0 shadow-md">
            <Building2 className="w-4 h-4 text-white" />
          </div>
        )}

        {/* Company Name */}
        {!isCollapsed && (
          <>
            <div className="flex-1 text-left min-w-0">
              <p className="text-sm font-bold text-gray-900 truncate">
                {currentCompany?.name || t('common.ui.companySwitcher.select')}
              </p>
              {currentCompany?.tradeName && currentCompany.tradeName !== currentCompany.name ? (
                <p className="text-xs text-gray-600 font-medium truncate">{currentCompany.tradeName}</p>
              ) : (
                <p className="text-xs text-gray-600 font-medium">{t('common.ui.companySwitcher.companiesCount', { count: companies.length })}</p>
              )}
            </div>

            {/* Dropdown Arrow */}
            <ChevronDown
              className={`w-4 h-4 text-gray-400 group-hover:text-teal-600 transition-all ${
                isOpen ? 'rotate-180 text-teal-600' : ''
              }`}
            />
          </>
        )}
      </button>

      {/* Dropdown Menu - positionné à droite pour éviter d'être coupé par le sidebar */}
      {isOpen && (
        <div
          className={`
            absolute z-[100] mt-2 bg-white/70 backdrop-blur-xl rounded-2xl
            border border-white/50 shadow-xl shadow-gray-300/20
            overflow-hidden w-[300px]
            ${isCollapsed ? 'left-full ml-2 top-0' : 'left-0'}
          `}
          style={{ minWidth: '280px' }}
        >
          {/* Header */}
          <div className="px-4 py-3 bg-white/30 border-b border-white/40">
            <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">
              {t('common.ui.companySwitcher.myCompanies')}
            </p>
          </div>

          {/* Companies List */}
          <div className="max-h-[300px] overflow-y-auto py-2">
            {companies.map((company) => (
              <button
                key={company.id}
                onClick={() => handleSwitchCompany(company.id)}
                disabled={switching}
                className={`
                  w-full flex items-center gap-3 px-4 py-3 cursor-pointer
                  hover:bg-teal-50/70 hover:pl-5 transition-all duration-200
                  ${company.id === currentCompanyId ? 'bg-teal-50/50 pl-5' : ''}
                `}
              >
                {/* Logo */}
                {company.logoUrl ? (
                  <img
                    src={company.logoUrl}
                    alt={company.name}
                    className="w-10 h-10 rounded-xl object-contain bg-white border border-gray-100"
                  />
                ) : (
                  <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-teal-600" />
                  </div>
                )}

                {/* Info */}
                <div className="flex-1 text-left min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-gray-900 truncate">{company.name}</p>
                    {company.id === currentCompanyId && (
                      <Check className="w-4 h-4 text-teal-600 flex-shrink-0" />
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    {getRoleBadge(company.role)}
                    {company.siret && (
                      <span className="text-xs text-gray-400 truncate">{company.siret}</span>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Footer - Quota info only */}
          {quota && quota.limit !== -1 && (
            <div className="px-4 py-2 bg-white/30 border-t border-white/40">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-600">{t('common.ui.companySwitcher.companiesUsed')}</span>
                <span className={`text-xs font-medium ${quota.canCreate ? 'text-gray-700' : 'text-amber-600'}`}>
                  {quota.current}/{quota.limit}
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CompanySwitcher;
